import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Plus,
  Edit,
  Trash2,
  Gauge,
  Building,
  Calendar,
  Zap,
  X,
  Filter,
  Grid,
  List,
  Eye,
  Download
  , ChevronUp, ChevronDown
} from 'lucide-react';
import { metersAPI, roomsAPI } from '../services/api';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid
} from 'recharts';
import { formatCurrency, getMonthName, formatDate, formatDateForInput } from '../utils/helpers';
import toast from 'react-hot-toast';
import DeleteConfirmModal from '../components/DeleteConfirmModal';

const MetersPage = () => {
  const [readings, setReadings] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [readingToDelete, setReadingToDelete] = useState(null);
  const [selectedReading, setSelectedReading] = useState(null);
  const [viewMode, setViewMode] = useState('list'); // 'list', 'grid' or 'chart'
  const [editingReading, setEditingReading] = useState(null);
  const [chartLayout, setChartLayout] = useState('vertical'); // 'vertical' or 'horizontal'
  const [formData, setFormData] = useState({
    room_id: '',
    reading_date: formatDateForInput(new Date()),
    meter_reading: '',
    bill_month: new Date().getMonth() + 1,
    bill_year: new Date().getFullYear()
  });
  const [filters, setFilters] = useState({
    room_id: '',
    year: new Date().getFullYear(),
    month: ''
  });
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth < 768 : false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  // Track viewport size to tune chart rendering for mobile vs desktop
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Custom tick renderer that adapts to light/dark theme and keeps rotation
  const CustomTick = ({ x, y, payload, angle = 0, isXAxis = false }) => {
    const isDark = typeof document !== 'undefined' && document.documentElement.classList.contains('dark');
    const fill = isDark ? '#E6F2FF' : '#0f172a';
    const fontSize = isMobile ? (isXAxis ? 12 : 12) : (isXAxis ? 14 : 14);
    const transform = angle ? `rotate(${angle} ${x} ${y})` : undefined;
    const dy = isXAxis ? 6 : 4;
    return (
      <text x={x} y={y + dy} transform={transform} textAnchor={isXAxis ? 'end' : 'end'} fill={fill} fontSize={fontSize} fontWeight={700}>
        {payload.value}
      </text>
    );
  };

  // Aggregate readings by billing period (month + year) for charting
  const aggregatedReadings = readings.reduce((map, r) => {
    const key = `${r.bill_year}-${String(r.bill_month).padStart(2, '0')}`;
    if (!map[key]) map[key] = { bill_year: r.bill_year, bill_month: r.bill_month, units: 0, charge: 0 };
    map[key].units += Number(r.units_consumed || 0);
    map[key].charge += Number(r.electricity_charge || 0);
    return map;
  }, {});

  const meterChartData = Object.values(aggregatedReadings)
    .sort((a, b) => (a.bill_year - b.bill_year) || (a.bill_month - b.bill_month))
    .map(d => {
      const shortMonth = getMonthName(d.bill_month).slice(0,3);
      const shortYear = String(d.bill_year).slice(-2);
      return {
        period: `${getMonthName(d.bill_month)} ${d.bill_year}`,
        label: `${shortMonth} ${shortYear}`,
        units: d.units,
        charge: d.charge || 0
      };
    });

  // Compute total units currently represented in the chart and the room label
  const totalUnits = meterChartData.reduce((sum, d) => sum + Number(d.units || 0), 0);
  const roomLabel = filters.room_id
    ? (() => {
        const found = rooms.find(r => r.id == filters.room_id || String(r.id) === String(filters.room_id));
        return found ? `Room ${found.room_number}` : `Room ${filters.room_id}`;
      })()
    : 'All Rooms';

  // Theme-aware axis label color
  const axisLabelFill = typeof document !== 'undefined' && document.documentElement.classList.contains('dark') ? '#E6F2FF' : '#0f172a';

  useEffect(() => {
    fetchRooms();
    fetchReadings();
  }, []);

  const sortedReadings = React.useMemo(() => {
    const list = readings.slice();
    if (!sortConfig.key) return list;
    const dir = sortConfig.direction === 'asc' ? 1 : -1;
    return list.sort((a, b) => {
      switch (sortConfig.key) {
        case 'period':
          if (a.bill_year !== b.bill_year) return (a.bill_year - b.bill_year) * dir;
          return (a.bill_month - b.bill_month) * dir;
        case 'meter_reading':
          return ((Number(a.meter_reading) || 0) - (Number(b.meter_reading) || 0)) * dir;
        case 'units':
          return ((Number(a.units_consumed) || 0) - (Number(b.units_consumed) || 0)) * dir;
        case 'unit_rate': {
          const av = Number(a.unit_rate ?? ((a.units_consumed > 0) ? (a.electricity_charge / a.units_consumed) : 0)) || 0;
          const bv = Number(b.unit_rate ?? ((b.units_consumed > 0) ? (b.electricity_charge / b.units_consumed) : 0)) || 0;
          return (av - bv) * dir;
        }
        case 'room': {
          const av = String(a.room_number || '');
          const bv = String(b.room_number || '');
          return av.localeCompare(bv, undefined, { numeric: true, sensitivity: 'base' }) * dir;
        }
        case 'tenant': {
          const av = String(a.tenant_name || '');
          const bv = String(b.tenant_name || '');
          return av.localeCompare(bv, undefined, { numeric: true, sensitivity: 'base' }) * dir;
        }
        case 'reading_date': {
          const av = a.reading_date ? new Date(a.reading_date).getTime() : 0;
          const bv = b.reading_date ? new Date(b.reading_date).getTime() : 0;
          return (av - bv) * dir;
        }
        case 'charge': {
          const av = Number(a.electricity_charge) || 0;
          const bv = Number(b.electricity_charge) || 0;
          return (av - bv) * dir;
        }
        default:
          return 0;
      }
    });
  }, [readings, sortConfig]);

  const renderSortIcon = (key) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === 'asc' ? (
      <ChevronUp className="w-3 h-3 inline-block ml-2" />
    ) : (
      <ChevronDown className="w-3 h-3 inline-block ml-2" />
    );
  };

  useEffect(() => {
    fetchReadings();
  }, [filters]);

  const fetchRooms = async () => {
    try {
      const response = await roomsAPI.getAll();
      setRooms(response.data.rooms);
    } catch (error) {
      console.error('Error fetching rooms:', error);
      toast.error('Failed to load rooms');
    }
  };

  const fetchReadings = async () => {
    try {
      const params = {};
      if (filters.room_id) params.room_id = filters.room_id;
      if (filters.year) params.year = filters.year;
      if (filters.month) params.month = filters.month;

      const response = await metersAPI.getAll(params);
      setReadings(response.data.readings);
    } catch (error) {
      console.error('Error fetching readings:', error);
      toast.error('Failed to load meter readings');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingReading) {
        await metersAPI.update(editingReading.id, formData);
        toast.success('Meter reading updated successfully');
      } else {
        await metersAPI.create(formData);
        toast.success('Meter reading created successfully');
      }
      
      fetchReadings();
      closeModal();
    } catch (error) {
      console.error('Error saving reading:', error);
      toast.error(error.response?.data?.error || 'Failed to save meter reading');
    }
  };

  const handleDelete = (reading) => {
    setReadingToDelete(reading);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      await metersAPI.delete(readingToDelete.id);
      toast.success('Meter reading deleted successfully');
      fetchReadings();
    } catch (error) {
      console.error('Error deleting reading:', error);
      toast.error('Failed to delete meter reading');
    }
  };


  const openModal = (reading = null) => {
    if (reading) {
      setEditingReading(reading);
      setFormData({
        room_id: reading.room_id,
        reading_date: formatDateForInput(reading.reading_date),
        meter_reading: reading.meter_reading,
        bill_month: reading.bill_month,
        bill_year: reading.bill_year
      });
    } else {
      setEditingReading(null);
      setFormData({
        room_id: '',
        reading_date: formatDateForInput(new Date()),
        meter_reading: '',
        bill_month: new Date().getMonth() + 1,
        bill_year: new Date().getFullYear()
      });
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingReading(null);
  };

  const openDetailModal = (reading) => {
    setSelectedReading(reading);
    setShowDetailModal(true);
  };

  const closeDetailModal = () => {
    setShowDetailModal(false);
    setSelectedReading(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const generateYears = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let year = currentYear; year >= currentYear - 5; year--) {
      years.push(year);
    }
    return years;
  };

  const handleDownloadPDF = () => {
    try {
      if (readings.length === 0) {
        toast.error('No meter readings to download');
        return;
      }

      // Get filtered readings
      const filteredReadings = readings;
      
      // Calculate totals
      const totalUnits = filteredReadings.reduce((sum, r) => sum + (r.units_consumed || 0), 0);
      const totalCharge = filteredReadings.reduce((sum, r) => sum + (r.electricity_charge || 0), 0);
      
      const reportTitle = 'Meter Readings Report';
      const filterInfo = [];
      if (filters.room_id) {
        const room = rooms.find(r => r.id == filters.room_id);
        filterInfo.push(`Room: ${room ? room.room_number : filters.room_id}`);
      }
      if (filters.year) filterInfo.push(`Year: ${filters.year}`);
      if (filters.month) filterInfo.push(`Month: ${getMonthName(parseInt(filters.month))}`);
      
      // Format date as "Wed 20-Jan-2025"
      const formatPDFDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        return `${days[date.getDay()]} ${date.getDate()}-${months[date.getMonth()]}-${date.getFullYear()}`;
      };
      
      const htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="UTF-8">
          <title>${reportTitle} - ${new Date().toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' })}</title>
          <style>
            @page {
              size: A4;
              margin: 15mm;
            }
            body { 
              font-family: Arial, sans-serif; 
              margin: 0;
              padding: 40px;
              background: white;
            }
            .header { 
              text-align: center; 
              margin-bottom: 30px; 
              border-bottom: 3px solid #3b82f6; 
              padding-bottom: 20px; 
            }
            .header h1 { 
              color: #1e40af; 
              margin: 0; 
              font-size: 28px; 
            }
            .header p { 
              color: #6b7280; 
              margin: 5px 0; 
            }
            .print-button {
              position: fixed;
              top: 20px;
              right: 20px;
              background: #3b82f6;
              color: white;
              border: none;
              padding: 12px 24px;
              border-radius: 8px;
              font-size: 16px;
              font-weight: 600;
              cursor: pointer;
              box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
              z-index: 1000;
              transition: background 0.3s;
            }
            .print-button:hover {
              background: #2563eb;
            }
            .filters { 
              background: #f3f4f6; 
              padding: 15px; 
              border-radius: 8px; 
              margin-bottom: 20px; 
            }
            .filters h3 { 
              margin: 0 0 10px 0; 
              color: #374151; 
              font-size: 16px; 
            }
            .filters p { 
              margin: 5px 0; 
              color: #6b7280; 
            }
            .summary { 
              display: grid; 
              grid-template-columns: repeat(3, 1fr); 
              gap: 15px; 
              margin-bottom: 30px; 
            }
            .summary-card { 
              background: #eff6ff; 
              padding: 15px; 
              border-radius: 8px; 
              text-align: center; 
              border-left: 4px solid #3b82f6; 
            }
            .summary-card h4 { 
              margin: 0; 
              color: #6b7280; 
              font-size: 14px; 
              font-weight: normal; 
            }
            .summary-card p { 
              margin: 10px 0 0 0; 
              color: #1e40af; 
              font-size: 24px; 
              font-weight: bold; 
            }
            table { 
              width: 100%; 
              border-collapse: collapse; 
              margin-top: 20px; 
              font-size: 12px;
            }
            th { 
              background: #1e40af; 
              color: white; 
              padding: 8px 6px; 
              text-align: left; 
              font-weight: 600; 
              font-size: 11px;
            }
            td { 
              padding: 6px 6px; 
              border-bottom: 1px solid #e5e7eb; 
              font-size: 11px;
            }
            tr:nth-child(even) { 
              background: #f9fafb; 
            }
            tr:hover { 
              background: #f3f4f6; 
            }
            .footer { 
              margin-top: 30px; 
              text-align: center; 
              color: #9ca3af; 
              font-size: 12px; 
              border-top: 1px solid #e5e7eb; 
              padding-top: 20px; 
            }
            @media print {
              .print-button {
                display: none;
              }
              body {
                padding: 0;
              }
              .summary-card,
              .filters,
              tr:nth-child(even) {
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
              }
            }
          </style>
        </head>
        <body>
          <button class="print-button" onclick="window.print()">🖨️ Print / Save as PDF</button>
          
          <div class="header">
            <h1>${reportTitle}</h1>
            <p>Generated on ${new Date().toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
          </div>
          
          ${filterInfo.length > 0 ? `
          <div class="filters">
            <h3>Applied Filters</h3>
            ${filterInfo.map(info => `<p>${info}</p>`).join('')}
          </div>
          ` : ''}
          
          <div class="summary">
            <div class="summary-card">
              <h4>Total Readings</h4>
              <p>${filteredReadings.length}</p>
            </div>
            <div class="summary-card">
              <h4>Total Units Consumed</h4>
              <p>${Math.round(totalUnits)} kWh</p>
            </div>
            <div class="summary-card">
              <h4>Total Electricity Charge</h4>
              <p>${formatCurrency(totalCharge)}</p>
            </div>
          </div>
          
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Room</th>
                <th>Tenant</th>
                <th>Period</th>
                <th>Prev.</th>
                <th>Curr.</th>
                <th>Units</th>
                <th>Rate</th>
                <th>Charge</th>
              </tr>
            </thead>
            <tbody>
              ${filteredReadings.map(reading => {
                const room = rooms.find(r => r.id === reading.room_id);
                const unitRate = reading.units_consumed > 0 ? (reading.electricity_charge || 0) / reading.units_consumed : 0;
                return `
                <tr>
                  <td>${formatPDFDate(reading.reading_date)}</td>
                  <td>R-${room ? room.room_number : reading.room_id}</td>
                  <td>${room && room.tenant_name ? room.tenant_name : 'Vacant'}</td>
                  <td>${getMonthName(reading.bill_month).slice(0, 3)} ${reading.bill_year}</td>
                  <td>${reading.previous_reading || 0}</td>
                  <td>${reading.meter_reading || 0}</td>
                  <td>${reading.units_consumed || 0}</td>
                  <td>${unitRate > 0 ? formatCurrency(unitRate) : 'N/A'}</td>
                  <td>${formatCurrency(reading.electricity_charge || 0)}</td>
                </tr>
              `}).join('')}
            </tbody>
          </table>
          
          <div class="footer">
            <p>Tenant Bills Management System © ${new Date().getFullYear()}</p>
          </div>
        </body>
        </html>
      `;

      // Open HTML in new window/tab
      const newWindow = window.open('', '_blank');
      newWindow.document.open();
      newWindow.document.write(htmlContent);
      newWindow.document.close();
      
      // Ensure title is set
      setTimeout(() => {
        if (newWindow && !newWindow.closed) {
          newWindow.document.title = `Meter Readings Report - ${new Date().toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' })}`;
        }
      }, 100);
      
      toast.success('Report opened in new tab! Click the print button to save as PDF.');
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Failed to generate report');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900 dark:text-secondary-100">Meter Readings</h1>
          <p className="text-secondary-600 dark:text-secondary-300">Record and manage electricity meter readings</p>
        </div>
        <div className="flex items-center space-x-3">
          {/* View Toggle */}
          <div className="flex rounded-lg overflow-hidden border border-secondary-300">
            <button
              onClick={() => setViewMode('list')}
              className={`px-3 py-2 flex items-center space-x-2 transition-colors ${
                viewMode === 'list'
                  ? 'bg-primary-500 text-white'
          : 'bg-white dark:bg-secondary-800 text-secondary-600 dark:text-secondary-300 hover:bg-secondary-50 dark:hover:bg-secondary-700'
              }`}
            >
              <List className="w-4 h-4" />
              <span className="hidden sm:inline">List</span>
            </button>
            <button
              onClick={() => setViewMode('grid')}
              className={`px-3 py-2 flex items-center space-x-2 transition-colors ${
                viewMode === 'grid'
                  ? 'bg-primary-500 text-white'
          : 'bg-white dark:bg-secondary-800 text-secondary-600 dark:text-secondary-300 hover:bg-secondary-50 dark:hover:bg-secondary-700'
              }`}
            >
              <Grid className="w-4 h-4" />
              <span className="hidden sm:inline">Grid</span>
            </button>
            <button
              onClick={() => setViewMode('chart')}
              className={`px-3 py-2 flex items-center space-x-2 transition-colors ${
                viewMode === 'chart'
                  ? 'bg-primary-500 text-white'
            : 'bg-white dark:bg-secondary-800 text-secondary-600 dark:text-secondary-300 hover:bg-secondary-50 dark:hover:bg-secondary-700'
              }`}
            >
              <Gauge className="w-4 h-4" />
              <span className="hidden sm:inline">Chart</span>
            </button>
          </div>
          
          <button
            onClick={() => openModal()}
            className="btn-primary"
          >
            <Plus className="w-4 h-4 mr-2" />
            <span className="hidden sm:inline">Add Reading</span>
            <span className="sm:hidden">Add</span>
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="flex items-center space-x-4">
          <Filter className="w-5 h-5 text-secondary-400 dark:text-secondary-300" />
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 flex-1">
            <div>
              <select
                name="room_id"
                value={filters.room_id}
                onChange={handleFilterChange}
                className="form-input"
              >
                <option value="">All Rooms</option>
                {rooms.map(room => (
                  <option key={room.id} value={room.id}>
                    Room {room.room_number}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <select
                name="year"
                value={filters.year}
                onChange={handleFilterChange}
                className="form-input"
              >
                {generateYears().map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
            <div>
              <select
                name="month"
                value={filters.month}
                onChange={handleFilterChange}
                className="form-input"
              >
                <option value="">All Months</option>
                {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
                  <option key={month} value={month}>
                    {getMonthName(month)}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <button
                onClick={handleDownloadPDF}
                className="btn-primary w-full flex items-center justify-center"
                title="Download PDF Report"
              >
                <Download className="w-4 h-4 mr-2" />
                Download PDF
              </button>
            </div>
            <div>
              <button
                onClick={() => setFilters({ room_id: '', year: new Date().getFullYear(), month: '' })}
                className="btn-outline w-full"
              >
                Clear Filters
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Readings Display */}
      {readings.length > 0 ? (
        viewMode === 'list' ? (
          // List View - Modern Table
          <div className="bg-white dark:bg-secondary-800 rounded-2xl shadow-sm border border-secondary-200 dark:border-secondary-700 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-secondary-50 dark:bg-secondary-700/50 border-b border-secondary-200 dark:border-secondary-600">
                    <th onClick={() => setSortConfig(prev => ({ key: 'room', direction: prev.key === 'room' && prev.direction === 'asc' ? 'desc' : 'asc' }))}
                      className="cursor-pointer px-5 py-3 text-left text-xs font-semibold text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">
                      Room {renderSortIcon('room')}
                    </th>
                    <th onClick={() => setSortConfig(prev => ({ key: 'tenant', direction: prev.key === 'tenant' && prev.direction === 'asc' ? 'desc' : 'asc' }))}
                      className="cursor-pointer px-5 py-3 text-left text-xs font-semibold text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">
                      Tenant {renderSortIcon('tenant')}
                    </th>
                    <th onClick={() => setSortConfig(prev => ({ key: 'reading_date', direction: prev.key === 'reading_date' && prev.direction === 'asc' ? 'desc' : 'asc' }))}
                      className="cursor-pointer px-5 py-3 text-left text-xs font-semibold text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">
                      Reading Date {renderSortIcon('reading_date')}
                    </th>
                    <th onClick={() => setSortConfig(prev => ({ key: 'period', direction: prev.key === 'period' && prev.direction === 'asc' ? 'desc' : 'asc' }))}
                      className="cursor-pointer px-5 py-3 text-left text-xs font-semibold text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">
                      Period {renderSortIcon('period')}
                    </th>
                    <th onClick={() => setSortConfig(prev => ({ key: 'meter_reading', direction: prev.key === 'meter_reading' && prev.direction === 'asc' ? 'desc' : 'asc' }))}
                      className="cursor-pointer px-5 py-3 text-left text-xs font-semibold text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">
                      Meter Readings {renderSortIcon('meter_reading')}
                    </th>
                    <th onClick={() => setSortConfig(prev => ({ key: 'units', direction: prev.key === 'units' && prev.direction === 'asc' ? 'desc' : 'asc' }))}
                      className="cursor-pointer px-5 py-3 text-left text-xs font-semibold text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">
                      Units {renderSortIcon('units')}
                    </th>
                    <th onClick={() => setSortConfig(prev => ({ key: 'unit_rate', direction: prev.key === 'unit_rate' && prev.direction === 'asc' ? 'desc' : 'asc' }))}
                      className="cursor-pointer px-5 py-3 text-left text-xs font-semibold text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">
                      Unit Rate {renderSortIcon('unit_rate')}
                    </th>
                    <th onClick={() => setSortConfig(prev => ({ key: 'charge', direction: prev.key === 'charge' && prev.direction === 'asc' ? 'desc' : 'asc' }))}
                      className="cursor-pointer px-5 py-3 text-left text-xs font-semibold text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">
                      Charge {renderSortIcon('charge')}
                    </th>
                    <th className="px-5 py-3 text-right text-xs font-semibold text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-secondary-100 dark:divide-secondary-700">
                  {sortedReadings.map((reading) => (
                    <motion.tr
                      key={reading.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="hover:bg-secondary-50 dark:hover:bg-secondary-700/30 transition-colors group cursor-pointer"
                      onClick={() => openDetailModal(reading)}
                    >
                      {/* Room */}
                      <td className="px-5 py-4">
                        <div className="flex items-center space-x-2.5">
                          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center flex-shrink-0">
                            <Building className="w-4 h-4 text-white" />
                          </div>
                          <span className="font-semibold text-secondary-900 dark:text-secondary-100 text-sm">{reading.room_number}</span>
                        </div>
                      </td>
                      {/* Tenant */}
                      <td className="px-5 py-4">
                        <span className="text-sm text-secondary-700 dark:text-secondary-300">
                          {reading.tenant_name || <span className="text-secondary-400 dark:text-secondary-500 italic">Vacant</span>}
                        </span>
                      </td>
                      {/* Reading Date */}
                      <td className="px-5 py-4">
                        <div className="flex items-center space-x-1.5 text-sm text-secondary-600 dark:text-secondary-400">
                          <Calendar className="w-3.5 h-3.5" />
                          <span>{formatDate(reading.reading_date, 'EEE dd-MMM-yyyy')}</span>
                        </div>
                      </td>
                      {/* Period */}
                      <td className="px-5 py-4">
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400">
                          {getMonthName(reading.bill_month)} {reading.bill_year}
                        </span>
                      </td>
                      {/* Meter Readings */}
                      <td className="px-5 py-4">
                        <div className="flex items-center space-x-2 text-sm">
                          <div className="text-center">
                            <span className="text-xs text-secondary-400 dark:text-secondary-500 block">Prev</span>
                            <span className="font-mono font-medium text-secondary-600 dark:text-secondary-300">{reading.previous_reading ?? 0}</span>
                          </div>
                          <span className="text-secondary-300 dark:text-secondary-600">→</span>
                          <div className="text-center">
                            <span className="text-xs text-secondary-400 dark:text-secondary-500 block">Curr</span>
                            <span className="font-mono font-semibold text-secondary-800 dark:text-secondary-200">{reading.meter_reading}</span>
                          </div>
                        </div>
                      </td>
                      {/* Units */}
                      <td className="px-5 py-4">
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                          <Gauge className="w-3 h-3 mr-1" />
                          {reading.units_consumed} kWh
                        </span>
                      </td>
                      {/* Unit Rate */}
                      <td className="px-5 py-4">
                        {reading.unit_rate ? (
                          <span className="text-sm font-medium text-emerald-600 dark:text-emerald-400">
                            {formatCurrency(reading.unit_rate)}<span className="text-xs text-secondary-400 dark:text-secondary-500">/unit</span>
                          </span>
                        ) : (
                          <span className="text-xs text-secondary-400 dark:text-secondary-500">—</span>
                        )}
                      </td>
                      {/* Charge */}
                      <td className="px-5 py-4">
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-sm font-bold bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
                          {formatCurrency(reading.electricity_charge)}
                        </span>
                      </td>
                      {/* Actions */}
                      <td className="px-5 py-4">
                        <div className="flex items-center justify-end space-x-1 opacity-0 group-hover:opacity-100 transition-opacity" onClick={(e) => e.stopPropagation()}>
                          <button
                            onClick={() => openDetailModal(reading)}
                            className="p-1.5 text-secondary-500 hover:bg-secondary-100 dark:hover:bg-secondary-700 rounded-lg transition-colors"
                            title="View Details"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => openModal(reading)}
                            className="p-1.5 text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg transition-colors"
                            title="Edit Reading"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(reading)}
                            className="p-1.5 text-danger-600 hover:bg-danger-50 dark:hover:bg-danger-900/20 rounded-lg transition-colors"
                            title="Delete Reading"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : viewMode === 'chart' ? (
          <div className="space-y-6">
            {/* KPI Summary Row */}
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-white dark:bg-secondary-800 rounded-2xl shadow-sm border border-secondary-200 dark:border-secondary-700 p-5 flex items-center space-x-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center flex-shrink-0">
                  <Gauge className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-xs text-secondary-500 dark:text-secondary-400 uppercase tracking-wider font-semibold">Total Readings</p>
                  <p className="text-2xl font-bold text-secondary-900 dark:text-secondary-100">{readings.length}</p>
                </div>
              </div>
              <div className="bg-white dark:bg-secondary-800 rounded-2xl shadow-sm border border-secondary-200 dark:border-secondary-700 p-5 flex items-center space-x-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-400 to-yellow-500 flex items-center justify-center flex-shrink-0">
                  <Zap className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-xs text-secondary-500 dark:text-secondary-400 uppercase tracking-wider font-semibold">Total Units</p>
                  <p className="text-2xl font-bold text-secondary-900 dark:text-secondary-100">{totalUnits} <span className="text-sm font-medium text-secondary-500">kWh</span></p>
                </div>
              </div>
              <div className="bg-white dark:bg-secondary-800 rounded-2xl shadow-sm border border-secondary-200 dark:border-secondary-700 p-5 flex items-center space-x-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-400 to-green-600 flex items-center justify-center flex-shrink-0">
                  <Building className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-xs text-secondary-500 dark:text-secondary-400 uppercase tracking-wider font-semibold">Scope</p>
                  <p className="text-2xl font-bold text-secondary-900 dark:text-secondary-100">{roomLabel}</p>
                </div>
              </div>
            </div>

            {/* Chart Card */}
            <div className="bg-white dark:bg-secondary-800 rounded-2xl shadow-sm border border-secondary-200 dark:border-secondary-700 overflow-hidden">
              <div className="bg-gradient-to-r from-violet-600 to-purple-700 px-6 py-5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-9 h-9 rounded-xl bg-white/20 flex items-center justify-center">
                      <Gauge className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-white">Meter Readings Chart</h3>
                      <p className="text-xs text-white/75">Monthly unit consumption by room</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setChartLayout('vertical')}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${chartLayout === 'vertical' ? 'bg-white text-violet-700' : 'bg-white/15 text-white hover:bg-white/25'}`}
                    >Vertical</button>
                    <button
                      onClick={() => setChartLayout('horizontal')}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${chartLayout === 'horizontal' ? 'bg-white text-violet-700' : 'bg-white/15 text-white hover:bg-white/25'}`}
                    >Horizontal</button>
                  </div>
                </div>
              </div>

            <div className="py-4">
              {/* Chart area: render vertical or horizontal based on chartLayout */}
              {chartLayout === 'vertical' ? (
                <>
                  {/* vertical chart: mobile then desktop */}
                  <div className="block md:hidden overflow-x-auto -mx-4 px-4">
                    <div style={{ width: Math.max(320, meterChartData.length * (isMobile ? 64 : 72)) }}>
                      <div style={{ height: isMobile ? 220 : 260 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={meterChartData} margin={{ top: 8, right: 8, left: 8, bottom: isMobile ? 36 : 40 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e6e6e6" />
                            <XAxis dataKey="label" tick={(props) => <CustomTick {...props} angle={isMobile ? -25 : -35} isXAxis={true} />} interval={0} height={isMobile ? 56 : 64} />
                            <YAxis tick={(props) => <CustomTick {...props} isXAxis={false} />} label={{ value: 'kWh', angle: 0, position: 'top', offset: -22, style: { fill: axisLabelFill, fontWeight: 800, fontSize: isMobile ? 12 : 14 } }} />
                            <Tooltip content={({ active, payload, label }) => {
                              if (!active || !payload || !payload.length) return null;
                              const value = payload[0].value;
                              const found = meterChartData.find(d => d.label === label);
                              const period = found ? found.period : label;
                              const charge = found ? found.charge : 0;
                              return (
                                <div style={{ background: 'rgba(15,23,42,0.95)', color: '#fff', padding: 8, borderRadius: 6, boxShadow: '0 6px 18px rgba(2,6,23,0.2)', fontSize: 13 }}>
                                  <div style={{ fontWeight: 700 }}>{period}</div>
                                  <div style={{ marginTop: 4 }}><span style={{ opacity: .9 }}>{value} kWh</span></div>
                                  <div style={{ marginTop: 6 }}><span style={{ opacity: .9 }}>{formatCurrency(charge)}</span></div>
                                </div>
                              );
                            }} />
                            <Bar dataKey="units" fill="#a8b910ff" radius={[6,6,0,0]} barSize={isMobile ? 18 : 28} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                    <div className="mt-3 px-2 text-sm text-secondary-600 dark:text-secondary-300 flex items-center justify-between">
                      <div><strong>Total:</strong> {totalUnits} kWh</div>
                      <div className="font-medium">{roomLabel}</div>
                    </div>
                  </div>

                  <div className="hidden md:block">
                    <div className="h-96">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={meterChartData} margin={{ top: 12, right: 20, left: 8, bottom: 60 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e6e6e6" />
                          <XAxis dataKey="label" tick={(props) => <CustomTick {...props} angle={-35} isXAxis={true} />} interval={0} height={80} />
                          <YAxis tick={(props) => <CustomTick {...props} isXAxis={false} />} label={{ value: 'kWh', angle: 0, position: 'top', offset: -10, style: { fill: axisLabelFill, fontWeight: 800, fontSize: 14 } }} />
                          <Tooltip content={({ active, payload, label }) => {
                            if (!active || !payload || !payload.length) return null;
                            const value = payload[0].value;
                            const found = meterChartData.find(d => d.label === label);
                            const period = found ? found.period : label;
                            const charge = found ? found.charge : 0;
                            return (
                              <div style={{ background: 'rgba(15,23,42,0.95)', color: '#fff', padding: 10, borderRadius: 6, boxShadow: '0 8px 22px rgba(2,6,23,0.25)', fontSize: 14 }}>
                                <div style={{ fontWeight: 800 }}>{period}</div>
                                <div style={{ marginTop: 6 }}><span style={{ opacity: .95 }}>{value} kWh</span></div>
                                <div style={{ marginTop: 6 }}><span style={{ opacity: .95 }}>{formatCurrency(charge)}</span></div>
                              </div>
                            );
                          }} />
                          <Bar dataKey="units" fill="#10b9b9ff" radius={[8,8,0,0]} barSize={36} />
                        </BarChart>
                      </ResponsiveContainer>
                      <div className="mt-4 px-3 text-sm text-secondary-600 dark:text-secondary-300 flex items-center justify-between">
                        <div><strong>Total:</strong> {totalUnits} kWh</div>
                        <div className="font-medium">{roomLabel}</div>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  {/* horizontal chart: mobile then desktop */}
                  <div className="block md:hidden overflow-x-auto -mx-4 px-4">
                    <div style={{ width: Math.max(320, meterChartData.length * (isMobile ? 140 : 180)) }}>
                      <div style={{ height: isMobile ? 240 : 320 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={meterChartData} layout="vertical" margin={{ top: 8, right: 8, left: 40, bottom: 8 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e6e6e6" />
                            <XAxis type="number" tick={{ fontSize: isMobile ? 12 : 13 }} />
                            <YAxis type="category" dataKey="label" tick={(props) => <CustomTick {...props} isXAxis={false} />} width={80} />
                            <Tooltip content={({ active, payload, label }) => {
                              if (!active || !payload || !payload.length) return null;
                              const value = payload[0].value;
                              const found = meterChartData.find(d => d.label === label);
                              const period = found ? found.period : label;
                              const charge = found ? found.charge : 0;
                              return (
                                <div style={{ background: 'rgba(15,23,42,0.95)', color: '#fff', padding: 8, borderRadius: 6, boxShadow: '0 6px 18px rgba(2,6,23,0.2)', fontSize: 13 }}>
                                  <div style={{ fontWeight: 700 }}>{period}</div>
                                  <div style={{ marginTop: 4 }}><span style={{ opacity: .9 }}>{value} kWh</span></div>
                                  <div style={{ marginTop: 6 }}><span style={{ opacity: .9 }}>{formatCurrency(charge)}</span></div>
                                </div>
                              );
                            }} />
                            <Bar dataKey="units" fill="#a23bf6ff" radius={[6,6,6,6]} barSize={isMobile ? 12 : 18} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                    <div className="mt-3 px-2 text-sm text-secondary-600 dark:text-secondary-300 flex items-center justify-between">
                      <div><strong>Total:</strong> {totalUnits} kWh</div>
                      <div className="font-medium">{roomLabel}</div>
                    </div>
                  </div>

                  <div className="hidden md:block">
                    <div className="h-96">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={meterChartData} layout="vertical" margin={{ top: 12, right: 20, left: 120, bottom: 20 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e6e6e6" />
                          <XAxis type="number" tick={{ fontSize: 14 }} />
                          <YAxis type="category" dataKey="label" tick={(props) => <CustomTick {...props} isXAxis={false} />} width={120} />
                          <Tooltip content={({ active, payload, label }) => {
                            if (!active || !payload || !payload.length) return null;
                            const value = payload[0].value;
                            const found = meterChartData.find(d => d.label === label);
                            const period = found ? found.period : label;
                            const charge = found ? found.charge : 0;
                            return (
                              <div style={{ background: 'rgba(15,23,42,0.95)', color: '#fff', padding: 10, borderRadius: 6, boxShadow: '0 8px 22px rgba(2,6,23,0.25)', fontSize: 14 }}>
                                <div style={{ fontWeight: 800 }}>{period}</div>
                                <div style={{ marginTop: 6 }}><span style={{ opacity: .95 }}>{value} units</span></div>
                                <div style={{ marginTop: 6 }}><span style={{ opacity: .95 }}>{formatCurrency(charge)}</span></div>
                              </div>
                            );
                          }} />
                          <Bar dataKey="units" fill="#3b82f6" radius={[8,8,8,8]} barSize={18} />
                        </BarChart>
                      </ResponsiveContainer>
                      <div className="mt-4 px-3 text-sm text-secondary-600 dark:text-secondary-300 flex items-center justify-between">
                        <div><strong>Total:</strong> {totalUnits} kWh</div>
                        <div className="font-medium">{roomLabel}</div>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
            </div>
          </div>
        ) : (
          // Grid View - Modern Cards
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {readings.map((reading) => (
              <motion.div
                key={reading.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white dark:bg-secondary-800 rounded-2xl shadow-sm border border-secondary-200 dark:border-secondary-700 overflow-hidden hover:shadow-md transition-shadow cursor-pointer group"
                onClick={() => openDetailModal(reading)}
              >
                {/* Gradient Header */}
                <div className="bg-gradient-to-r from-violet-600 to-purple-700 p-5">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="w-11 h-11 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
                        <Gauge className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-white leading-tight">Room {reading.room_number}</h3>
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-white/25 text-white mt-1">
                          {getMonthName(reading.bill_month)} {reading.bill_year}
                        </span>
                      </div>
                    </div>
                    {/* Action buttons */}
                    <div className="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={() => openDetailModal(reading)}
                        className="p-1.5 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
                        title="View Details"
                      >
                        <Eye className="w-3.5 h-3.5 text-white" />
                      </button>
                      <button
                        onClick={() => openModal(reading)}
                        className="p-1.5 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
                        title="Edit Reading"
                      >
                        <Edit className="w-3.5 h-3.5 text-white" />
                      </button>
                      <button
                        onClick={() => handleDelete(reading)}
                        className="p-1.5 bg-white/20 hover:bg-red-500/50 rounded-lg transition-colors"
                        title="Delete Reading"
                      >
                        <Trash2 className="w-3.5 h-3.5 text-white" />
                      </button>
                    </div>
                  </div>
                  {/* Charge hero */}
                  <div className="mt-4">
                    <p className="text-xs text-white/70 uppercase tracking-wider">Electricity Charge</p>
                    <p className="text-2xl font-bold text-white">{formatCurrency(reading.electricity_charge)}</p>
                  </div>
                </div>

                {/* Body */}
                <div className="p-5 space-y-4">
                  {/* Tenant */}
                  {reading.tenant_name && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-secondary-500 dark:text-secondary-400">Tenant</span>
                      <span className="font-semibold text-secondary-800 dark:text-secondary-200">{reading.tenant_name}</span>
                    </div>
                  )}

                  {/* Reading Date */}
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-secondary-500 dark:text-secondary-400 flex items-center space-x-1">
                      <Calendar className="w-3.5 h-3.5" />
                      <span>Reading Date</span>
                    </span>
                    <span className="font-medium text-secondary-700 dark:text-secondary-300">{formatDate(reading.reading_date, 'EEEE dd-MMM-yyyy')}</span>
                  </div>

                  {/* Meter Readings */}
                  <div className="bg-secondary-50 dark:bg-secondary-700/40 rounded-xl p-3">
                    <p className="text-xs text-secondary-500 dark:text-secondary-400 uppercase tracking-wider font-semibold mb-2">Meter Readings</p>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <span className="text-xs text-secondary-400 dark:text-secondary-500">Previous</span>
                        <p className="font-mono font-semibold text-secondary-700 dark:text-secondary-300">{reading.previous_reading ?? 0} <span className="text-xs font-normal">kWh</span></p>
                      </div>
                      <div>
                        <span className="text-xs text-secondary-400 dark:text-secondary-500">Current</span>
                        <p className="font-mono font-semibold text-secondary-800 dark:text-secondary-200">{reading.meter_reading} <span className="text-xs font-normal">kWh</span></p>
                      </div>
                    </div>
                  </div>

                  {/* Units & Rate */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="text-center bg-amber-50 dark:bg-amber-900/20 rounded-xl py-3">
                      <p className="text-xs text-amber-600 dark:text-amber-400 font-semibold uppercase tracking-wide">Units</p>
                      <p className="text-lg font-bold text-amber-700 dark:text-amber-300">{reading.units_consumed}</p>
                      <p className="text-xs text-amber-500 dark:text-amber-500">kWh</p>
                    </div>
                    <div className="text-center bg-emerald-50 dark:bg-emerald-900/20 rounded-xl py-3">
                      <p className="text-xs text-emerald-600 dark:text-emerald-400 font-semibold uppercase tracking-wide">Rate</p>
                      <p className="text-lg font-bold text-emerald-700 dark:text-emerald-300">{reading.unit_rate ? formatCurrency(reading.unit_rate) : '—'}</p>
                      <p className="text-xs text-emerald-500 dark:text-emerald-500">per unit</p>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )
      ) : (
        <div className="text-center py-12">
          <Gauge className="w-16 h-16 text-secondary-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-secondary-900 dark:text-secondary-100 mb-2">No meter readings found</h3>
          <p className="text-secondary-600 dark:text-secondary-300 mb-4">Start by adding your first meter reading</p>
          <button
            onClick={() => openModal()}
            className="btn-primary"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add First Reading
          </button>
        </div>
      )}

      {/* Modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={closeModal}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white dark:bg-secondary-800 rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto border border-secondary-700"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-bold text-secondary-900 dark:text-secondary-100">
                    {editingReading ? 'Edit Meter Reading' : 'Add New Meter Reading'}
                  </h2>
                  <button
                    onClick={closeModal}
                    className="p-2 hover:bg-secondary-100 rounded-lg transition-colors"
                  >
                    <X className="w-5 h-5 text-secondary-400 dark:text-secondary-300" />
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="form-label">Room</label>
                    <select
                      name="room_id"
                      value={formData.room_id}
                      onChange={handleChange}
                      className="form-input"
                      required
                    >
                      <option value="">Select a room</option>
                      {rooms.map(room => (
                        <option key={room.id} value={room.id}>
                          Room {room.room_number} - {room.tenant_name || 'Vacant'}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="form-label">Reading Date</label>
                    <input
                      type="date"
                      name="reading_date"
                      value={formData.reading_date}
                      onChange={handleChange}
                      className="form-input"
                      required
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="form-label">Bill Month</label>
                      <select
                        name="bill_month"
                        value={formData.bill_month}
                        onChange={handleChange}
                        className="form-input"
                        required
                      >
                        {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
                          <option key={month} value={month}>
                            {getMonthName(month)}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="form-label">Bill Year</label>
                      <select
                        name="bill_year"
                        value={formData.bill_year}
                        onChange={handleChange}
                        className="form-input"
                        required
                      >
                        {generateYears().map(year => (
                          <option key={year} value={year}>{year}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="form-label">Meter Reading</label>
                    <input
                      type="number"
                      name="meter_reading"
                      value={formData.meter_reading}
                      onChange={handleChange}
                      className="form-input"
                      placeholder="e.g., 12500"
                      min="0"
                      required
                    />
                    <p className="text-xs text-secondary-500 dark:text-secondary-300 mt-1">
                      Enter the current reading from the electricity meter
                    </p>
                  </div>

                  <div className="flex items-center justify-end space-x-3 pt-4">
                    <button
                      type="button"
                      onClick={closeModal}
                      className="btn-outline"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn-primary"
                    >
                      {editingReading ? 'Update Reading' : 'Create Reading'}
                    </button>
                  </div>
                </form>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Detail Modal */}
      <AnimatePresence>
        {showDetailModal && selectedReading && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={closeDetailModal}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0, y: 10 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 10 }}
              className="bg-white dark:bg-secondary-800 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-secondary-200 dark:border-secondary-700"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Gradient Header */}
              <div className="bg-gradient-to-r from-violet-600 to-purple-700 rounded-t-2xl px-6 py-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center flex-shrink-0">
                      <Gauge className="w-7 h-7 text-white" />
                    </div>
                    <div>
                      <p className="text-xs text-white/70 uppercase tracking-widest font-semibold mb-0.5">Meter Reading</p>
                      <h2 className="text-2xl font-bold text-white">Room {selectedReading.room_number}</h2>
                      {selectedReading.tenant_name && (
                        <p className="text-sm text-white/80 mt-0.5">{selectedReading.tenant_name}</p>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={closeDetailModal}
                    className="p-2 bg-white/15 hover:bg-white/25 rounded-xl transition-colors flex-shrink-0"
                  >
                    <X className="w-5 h-5 text-white" />
                  </button>
                </div>

                {/* Hero Stats */}
                <div className="mt-5 grid grid-cols-3 gap-3">
                  <div className="bg-white/15 rounded-xl px-4 py-3">
                    <p className="text-xs text-white/70 uppercase tracking-wider">Units Consumed</p>
                    <p className="text-xl font-bold text-white mt-0.5">{selectedReading.units_consumed} <span className="text-sm font-medium">kWh</span></p>
                  </div>
                  <div className="bg-white/15 rounded-xl px-4 py-3">
                    <p className="text-xs text-white/70 uppercase tracking-wider">Unit Rate</p>
                    <p className="text-xl font-bold text-white mt-0.5">
                      {selectedReading.unit_rate ? formatCurrency(selectedReading.unit_rate) : '—'}
                      <span className="text-sm font-medium">/unit</span>
                    </p>
                  </div>
                  <div className="bg-white/15 rounded-xl px-4 py-3">
                    <p className="text-xs text-white/70 uppercase tracking-wider">Electricity Charge</p>
                    <p className="text-xl font-bold text-white mt-0.5">{formatCurrency(selectedReading.electricity_charge)}</p>
                  </div>
                </div>
              </div>

              {/* Body */}
              <div className="p-6 space-y-6">

                {/* Meter Readings */}
                <div>
                  <p className="text-xs text-secondary-500 dark:text-secondary-400 uppercase tracking-wider font-semibold mb-3">Meter Readings</p>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-secondary-50 dark:bg-secondary-700/40 rounded-xl p-4">
                      <p className="text-xs text-secondary-500 dark:text-secondary-400 font-medium uppercase tracking-wide mb-1">Previous Reading</p>
                      <p className="text-2xl font-bold font-mono text-secondary-800 dark:text-secondary-100">
                        {selectedReading.previous_reading || 0}
                      </p>
                      <p className="text-xs text-secondary-400 dark:text-secondary-500 mt-0.5">kWh</p>
                    </div>
                    <div className="bg-secondary-50 dark:bg-secondary-700/40 rounded-xl p-4">
                      <p className="text-xs text-secondary-500 dark:text-secondary-400 font-medium uppercase tracking-wide mb-1">Current Reading</p>
                      <p className="text-2xl font-bold font-mono text-secondary-800 dark:text-secondary-100">
                        {selectedReading.meter_reading}
                      </p>
                      <p className="text-xs text-secondary-400 dark:text-secondary-500 mt-0.5">kWh</p>
                    </div>
                  </div>
                </div>

                {/* Reading Details */}
                <div>
                  <p className="text-xs text-secondary-500 dark:text-secondary-400 uppercase tracking-wider font-semibold mb-3">Reading Details</p>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between px-4 py-3 bg-secondary-50 dark:bg-secondary-700/40 rounded-xl">
                      <div className="flex items-center space-x-2 text-secondary-600 dark:text-secondary-300">
                        <Calendar className="w-4 h-4" />
                        <span className="text-sm font-medium">Reading Date</span>
                      </div>
                      <span className="text-sm font-semibold text-secondary-800 dark:text-secondary-100">
                        {formatDate(selectedReading.reading_date)}
                      </span>
                    </div>

                    <div className="flex items-center justify-between px-4 py-3 bg-secondary-50 dark:bg-secondary-700/40 rounded-xl">
                      <div className="flex items-center space-x-2 text-secondary-600 dark:text-secondary-300">
                        <Zap className="w-4 h-4" />
                        <span className="text-sm font-medium">Billing Period</span>
                      </div>
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400">
                        {getMonthName(selectedReading.bill_month)} {selectedReading.bill_year}
                      </span>
                    </div>

                    <div className="flex items-center justify-between px-4 py-3 bg-secondary-50 dark:bg-secondary-700/40 rounded-xl">
                      <div className="flex items-center space-x-2 text-secondary-600 dark:text-secondary-300">
                        <Building className="w-4 h-4" />
                        <span className="text-sm font-medium">Room</span>
                      </div>
                      <span className="text-sm font-semibold text-secondary-800 dark:text-secondary-100">
                        Room {selectedReading.room_number}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Calculation Box */}
                {selectedReading.unit_rate && (
                  <div className="px-4 py-3 bg-violet-50 dark:bg-violet-900/20 rounded-xl border border-violet-100 dark:border-violet-800">
                    <p className="text-sm text-violet-800 dark:text-violet-300">
                      <strong>Calculation:</strong> {selectedReading.units_consumed} units × {formatCurrency(selectedReading.unit_rate)}/unit = {formatCurrency(selectedReading.electricity_charge)}
                    </p>
                  </div>
                )}

                {/* Actions */}
                <div className="flex items-center justify-end space-x-3 pt-2 border-t border-secondary-100 dark:border-secondary-700">
                  <button
                    onClick={closeDetailModal}
                    className="btn-outline"
                  >
                    Close
                  </button>
                  <button
                    onClick={() => {
                      closeDetailModal();
                      openModal(selectedReading);
                    }}
                    className="btn-primary"
                  >
                    <Edit className="w-4 h-4 mr-2" />
                    Edit Reading
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={confirmDelete}
        itemName={readingToDelete ? `Meter Reading from ${formatDate(readingToDelete.reading_date)}` : ''}
        itemType="meter reading"
      />
    </div>
  );
};

export default MetersPage;
